/**
 * Seasonality detection via the autocorrelation function (ACF).
 *
 * The series is resampled onto a uniform grid and linearly detrended (a pure
 * trend autocorrelates at every lag and would otherwise masquerade as a cycle).
 * We then compute the ACF up to half the series length and take the dominant
 * cycle to be the strongest *local* ACF peak that (a) clears a 95% white-noise
 * significance bound and (b) sits past the ACF's initial descent — the peak at
 * lag 0 always dominates, so we look for the first genuine repeat beyond it.
 *
 * Separately — and only when the series spans at least two days — we bucket the
 * raw points by hour-of-day (UTC) and report the hours of highest and lowest
 * average value. This is the "peak-of-day" the agent can narrate directly.
 *
 * Full STL/loess decomposition is intentionally deferred; this is the honest
 * minimum that surfaces a period and a daily phase without over-claiming.
 */
import dayjs from 'dayjs';
import { linearRegression, mean } from 'simple-statistics';

import { MS_PER_DAY, MS_PER_HOUR } from '#core/lib/utils/date.ts';
import { env } from '#core/lib/utils/env.ts';

import { EPSILON } from './constants.ts';
import { medianStepMs, resampleUniform } from './resample.ts';
import type { SeasonalityResult, TimeseriesPoint } from './types.ts';

/** Minimum resampled points before an ACF is worth computing. */
const MIN_SAMPLES_FOR_ACF = 8;
/** Hour-of-day analysis needs at least this many days of span to be meaningful. */
const MIN_DAYS_FOR_HOURLY = 2;
/** 95% two-sided white-noise band for the ACF is ±this/√n. */
const WHITE_NOISE_Z = 1.96;

const NO_SEASON: SeasonalityResult = {
  label: null,
  dominantPeriodHours: null,
  strength: 0,
  peakHour: null,
  troughHour: null,
};

/** Hour-of-day (0–23, in `env.displayTz`) of the highest- and lowest-mean buckets. */
function hourlyExtremes(points: TimeseriesPoint[]): Pick<SeasonalityResult, 'peakHour' | 'troughHour'> {
  const byHour = points.reduce<Map<number, number[]>>((acc, p) => {
    const hour = dayjs(p.timestamp).tz(env.displayTz).hour();
    const bucket = acc.get(hour) ?? [];
    bucket.push(p.value);
    acc.set(hour, bucket);
    return acc;
  }, new Map());

  const means = Array.from(byHour, ([hour, values]) => ({ hour, mean: mean(values) }));
  const first = means[0];
  if (!first) {
    return { peakHour: null, troughHour: null };
  }
  const peak = means.reduce((best, h) => (h.mean > best.mean ? h : best), first);
  const trough = means.reduce((best, h) => (h.mean < best.mean ? h : best), first);
  return { peakHour: peak.hour, troughHour: trough.hour };
}

/** Autocorrelation at lags 1..maxLag of an already mean-centred (detrended) series. */
function autocorrelations(devs: number[], denom: number, maxLag: number): number[] {
  return Array.from({ length: maxLag }, (_, idx) => {
    const lag = idx + 1;
    const cov = devs.slice(0, devs.length - lag).reduce((sum, d, t) => sum + d * (devs[t + lag] ?? 0), 0);
    return cov / denom;
  });
}

/**
 * The strongest significant local maximum of the ACF that sits past the initial
 * descent. `lag` is 0 when no qualifying cycle exists. The descent guard skips
 * the dominant lag-0 neighbourhood, where a smooth signal's ACF is always high.
 */
function dominantCycle(acf: number[], bound: number): { lag: number; value: number } {
  // End of the initial descent: first lag whose ACF rises into the next.
  const firstRise = acf.findIndex((v, i) => v < (acf[i + 1] ?? Number.NEGATIVE_INFINITY));
  return acf.reduce(
    (best, value, i) => {
      const prev = acf[i - 1] ?? Number.NEGATIVE_INFINITY;
      const next = acf[i + 1] ?? Number.NEGATIVE_INFINITY;
      const isLocalMax = value >= prev && value >= next;
      const qualifies = firstRise >= 0 && i > firstRise && isLocalMax && value > bound && value > best.value;
      return qualifies ? { lag: i + 1, value } : best;
    },
    { lag: 0, value: 0 },
  );
}

/** Render a cycle length as plain text: about daily / weekly / every N days / every Nh. */
function describePeriod(hours: number): string {
  if (Math.abs(hours - 24) <= 2) {
    return 'repeats about daily';
  }
  if (Math.abs(hours - 168) <= 12) {
    return 'repeats about weekly';
  }
  if (hours >= 48) {
    return `repeats about every ${Math.round(hours / 24)} days`;
  }
  return `repeats about every ${Math.round(hours)}h`;
}

/** Plain-language phrasing of the cycle and daily peak; null when neither is known. */
function seasonalityLabel(periodHours: number | null, peakHour: number | null): string | null {
  const cycle = periodHours === null ? null : describePeriod(periodHours);
  const peak = peakHour === null ? null : `usually peaks around ${String(peakHour).padStart(2, '0')}:00`;
  if (cycle && peak) {
    return `${cycle}, ${peak}`;
  }
  return cycle ?? peak;
}

/** Detect the dominant cycle and daily phase of a series. */
export function computeSeasonality(points: TimeseriesPoint[]): SeasonalityResult {
  const first = points[0];
  const last = points.at(-1);
  if (!first || !last) {
    return NO_SEASON;
  }

  const spanMs = last.timestamp - first.timestamp;
  const hourly =
    spanMs >= MIN_DAYS_FOR_HOURLY * MS_PER_DAY ? hourlyExtremes(points) : { peakHour: null, troughHour: null };

  const step = medianStepMs(points);
  if (step <= 0) {
    return { ...NO_SEASON, ...hourly };
  }
  const count = Math.floor(spanMs / step) + 1;
  if (count < MIN_SAMPLES_FOR_ACF) {
    return { ...NO_SEASON, ...hourly };
  }

  // Detrend so a slope doesn't read as perfect autocorrelation at every lag.
  const resampled = resampleUniform(points, first.timestamp, step, count);
  const { m, b } = linearRegression(resampled.map((v, i) => [i, v]));
  const devs = resampled.map((v, i) => v - (m * i + b));
  const denom = devs.reduce((sum, d) => sum + d * d, 0);
  if (denom <= EPSILON) {
    return { ...NO_SEASON, ...hourly };
  }

  const acf = autocorrelations(devs, denom, Math.floor(count / 2));
  const dominant = dominantCycle(acf, WHITE_NOISE_Z / Math.sqrt(count));

  const dominantPeriodHours = dominant.lag > 0 ? (dominant.lag * step) / MS_PER_HOUR : null;
  return {
    label: seasonalityLabel(dominantPeriodHours, hourly.peakHour),
    dominantPeriodHours,
    strength: dominant.value,
    ...hourly,
  };
}
